/*
 * The threshold of Spore analysis's step 1 (components/workbench/
 * microscopy/MicroscopyMode.js), as three filters a pixel must pass to be in the
 * preview, all on the analysis grid and pure, like detect.js:
 *
 *   intensity  the grey level (detect.grayscale: Rec. 601 luma, blurred) at
 *              or above `min` and at or below `max` -- or, with `outside`,
 *              below `min` or above `max`. Auto (Otsu) is the band
 *              from 0 to Otsu's level for dark spores, or from it to 255 for
 *              light ones (bandFromAuto), which is what the old single
 *              slider and "Spores are" did; Auto (colourless spores) is the
 *              field's own level, kept outside (detect.autoDiffers).
 *   colour     optional: the pixel's hue, saturation and value within a
 *              tolerance of a colour picked off the picture with the
 *              eyedropper (pickColour: the mean of a 3 x 3 patch), so
 *              stained or pigmented spores can be told from grey debris.
 *              Tolerance T (0-100): hue within T x 1.8 degrees, saturation
 *              and value within T / 100. A picked colour with too little
 *              saturation to have a hue (under GREY_SATURATION) is matched
 *              on saturation and value only.
 *   size       optional: each 8-connected particle of what passed the two
 *              above, kept only when its area is within [minPx, maxPx] grid
 *              pixels -- dust under the minimum, clumps of touching spores
 *              over the maximum. What is left out is returned as `rejected`,
 *              drawn grey on the slide, and never applied to the mask.
 *
 * Kept OUTSIDE the band (colourless spores), the band only finds the
 * particles; each one's edge is then set half-way between the field (the
 * band's middle) and its own darkest rim (halfwayEdges), where an edge is: a
 * band near the field's noise would take the blurred skirt of every rim too,
 * and measure each spore a pixel too wide all round.
 *
 * The preview is what "Apply to mask" adds: the particles' enclosed holes are
 * filled first (detect.fillSmallHoles, as detect.applyThreshold does), so a
 * spore seen as a dark wall round a light centre is sized by its whole area,
 * not its wall's. The scale bar's corner (`excludeRect`) leaves out every
 * particle that reaches into it, whole -- as the frame of the picture does
 * when the mask is analysed -- so a spore across its edge is never cut into a
 * smaller one; those are `rejected` too.
 *
 * The page converts an area typed in um^2 (with a scale) or image px^2
 * (without) to grid pixels with areaToGridPx, the inverse of what
 * detect.analyseMask reports, and back with gridPxToArea.
 *
 * With `allow` (a grid mask: Culture analysis passes the dish), a pixel
 * outside it never passes, so a particle is sized by its part inside.
 */
import { labelComponents } from '../annotation/raster';
import { DEFAULTS as DETECT, fillSmallHoles } from './detect';

export const GREY_SATURATION = 0.15;
export const TOLERANCE_DEFAULT = 25;

/** Hue in degrees [0, 360), saturation and value in [0, 1]. */
export function rgbToHsv(r, g, b) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === rn) h = 60 * (((gn - bn) / d) % 6);
    else if (max === gn) h = 60 * ((bn - rn) / d + 2);
    else h = 60 * ((rn - gn) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

/** The whole picture in HSV: three Float32Arrays of w * h. */
export function hsvImage(rgba, w, h) {
  const n = w * h;
  const hue = new Float32Array(n);
  const sat = new Float32Array(n);
  const val = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    const c = rgbToHsv(rgba[i * 4], rgba[i * 4 + 1], rgba[i * 4 + 2]);
    hue[i] = c.h;
    sat[i] = c.s;
    val[i] = c.v;
  }
  return { h: hue, s: sat, v: val };
}

/**
 * The colour under (x, y) on the grid: the mean of the (2r+1)^2 patch around
 * it, clipped to the picture. {r, g, b, h, s, v, hex}, or null outside it.
 */
export function pickColour(rgba, w, h, x, y, radius = 1) {
  const cx = Math.floor(x);
  const cy = Math.floor(y);
  if (!(cx >= 0 && cy >= 0 && cx < w && cy < h)) return null;
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let yy = Math.max(0, cy - radius); yy <= Math.min(h - 1, cy + radius); yy += 1) {
    for (let xx = Math.max(0, cx - radius); xx <= Math.min(w - 1, cx + radius); xx += 1) {
      const i = (yy * w + xx) * 4;
      r += rgba[i];
      g += rgba[i + 1];
      b += rgba[i + 2];
      n += 1;
    }
  }
  const rgb = [r / n, g / n, b / n].map(Math.round);
  const hex = `#${rgb.map(c => c.toString(16).padStart(2, '0')).join('')}`;
  return { r: rgb[0], g: rgb[1], b: rgb[2], ...rgbToHsv(rgb[0], rgb[1], rgb[2]), hex };
}

/** What tolerance T (0-100) allows: {hue: degrees, sat, val}. */
export function colourTolerances(tolerance) {
  const t = Math.max(0, Math.min(100, Number(tolerance) || 0));
  return { hue: t * 1.8, sat: t / 100, val: t / 100 };
}

const hueGap = (a, b) => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

/** Whether one HSV value is within the tolerance of the picked colour. */
export function colourMatches(h, s, v, colour, tol) {
  if (Math.abs(s - colour.s) > tol.sat || Math.abs(v - colour.v) > tol.val) return false;
  if (colour.s < GREY_SATURATION) return true;
  return hueGap(h, colour.h) <= tol.hue;
}

/** Auto (Otsu) as a band: {threshold, polarity} from detect.autoThreshold -> {min, max}. */
export function bandFromAuto(auto) {
  if (!auto) return { min: 0, max: 128 };
  return auto.polarity === 'light' ? { min: auto.threshold, max: 255 } : { min: 0, max: auto.threshold };
}

/**
 * The band in the old single-slider terms, for the saved parameters: a band
 * from 0 is dark spores at its top, one to 255 light spores at its bottom;
 * anything else is a band ({polarity: 'band', threshold: null}), and one kept
 * outside is {polarity: 'outside', threshold: null}.
 */
export function polarityOfBand({ min, max, outside = false }) {
  if (outside) return { polarity: 'outside', threshold: null };
  if (min <= 0) return { polarity: 'dark', threshold: max };
  if (max >= 255) return { polarity: 'light', threshold: min };
  return { polarity: 'band', threshold: null };
}

/**
 * The pixels passing the intensity band (between its ends, or with `outside`
 * beyond them) and, when `colour` is given, the colour; with `allow`, only
 * pixels it holds.
 */
export function bandMask(gray, w, h, { min = 0, max = 255, outside = false, colour = null, tolerance = TOLERANCE_DEFAULT,
                                      hsv = null, allow = null } = {}) {
  const out = new Uint8Array(w * h);
  const tol = colour ? colourTolerances(tolerance) : null;
  const byColour = !!colour && !!hsv;
  for (let i = 0; i < out.length; i += 1) {
    if (allow && !allow[i]) continue;
    const g = gray[i];
    if ((g < min || g > max) !== outside) continue;
    if (byColour && !colourMatches(hsv.h[i], hsv.s[i], hsv.v[i], colour, tol)) continue;
    out[i] = 1;
  }
  return out;
}

/** 1 for each label with a pixel whose centre is in `rect` (image fractions). */
export function labelsInRect(labels, w, h, count, rect) {
  const hit = new Uint8Array(count + 1);
  const x0 = Math.max(0, Math.ceil(rect.x * w - 0.5));
  const x1 = Math.min(w, Math.ceil((rect.x + rect.w) * w - 0.5));
  const y0 = Math.max(0, Math.ceil(rect.y * h - 0.5));
  const y1 = Math.min(h, Math.ceil((rect.y + rect.h) * h - 0.5));
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) hit[labels[y * w + x]] = 1;
  }
  hit[0] = 0;
  return hit;
}

/**
 * Each 8-connected particle of `mask` kept when its pixel count is within
 * [minPx, maxPx] (either may be null: no bound). {mask, rejected, kept,
 * small, large}: the particles kept and left out, as masks and counts.
 */
export function filterBySize(mask, w, h, { minPx = null, maxPx = null } = {}) {
  const { labels, sizes, count } = labelComponents(mask, w, h, 8);
  const verdict = new Uint8Array(count + 1);       // 1 kept, 2 too small, 3 too large
  let kept = 0;
  let small = 0;
  let large = 0;
  for (let l = 1; l <= count; l += 1) {
    if (minPx !== null && sizes[l] < minPx) {
      verdict[l] = 2;
      small += 1;
    } else if (maxPx !== null && sizes[l] > maxPx) {
      verdict[l] = 3;
      large += 1;
    } else {
      verdict[l] = 1;
      kept += 1;
    }
  }
  const out = new Uint8Array(w * h);
  const rejected = new Uint8Array(w * h);
  for (let i = 0; i < labels.length; i += 1) {
    const v = verdict[labels[i]];
    if (v === 1) out[i] = 1;
    else if (v) rejected[i] = 1;
  }
  return { mask: out, rejected, kept, small, large };
}

//: halfwayEdges: a particle's darkest rim is this quantile of its grey levels (its brightest centre, 1 - it)
export const EDGE_QUANTILE = 0.05;

/**
 * Each particle of `mask` (found outside the band round `level`) cut again
 * half-way from `level` to its own darkest rim -- and, on the bright side,
 * to its own brightest centre -- then its holes filled. A particle that
 * keeps under half its pixels that way (a faint rim that breaks) keeps its
 * first outline instead.
 */
export function halfwayEdges(gray, mask, w, h, level) {
  const { labels, count } = labelComponents(mask, w, h, 8);
  if (!count) return mask.slice();
  const hist = new Uint32Array((count + 1) * 256);
  const size = new Uint32Array(count + 1);
  for (let i = 0; i < labels.length; i += 1) {
    const l = labels[i];
    if (!l) continue;
    hist[l * 256 + Math.max(0, Math.min(255, Math.round(gray[i])))] += 1;
    size[l] += 1;
  }
  const below = new Float32Array(count + 1);
  const above = new Float32Array(count + 1);
  for (let l = 1; l <= count; l += 1) {
    const quantile = (q) => {
      let seen = 0;
      for (let v = 0; v < 256; v += 1) {
        seen += hist[l * 256 + v];
        if (seen > q * size[l]) return v;
      }
      return 255;
    };
    const dark = quantile(EDGE_QUANTILE);
    const bright = quantile(1 - EDGE_QUANTILE);
    below[l] = dark < level ? level - (level - dark) / 2 : -Infinity;
    above[l] = bright > level ? level + (bright - level) / 2 : Infinity;
  }
  const cut = new Uint8Array(w * h);
  for (let i = 0; i < labels.length; i += 1) {
    const l = labels[i];
    if (l && (gray[i] <= below[l] || gray[i] >= above[l])) cut[i] = 1;
  }
  const out = fillSmallHoles(cut, w, h, Math.max(1, Math.round(DETECT.maxHoleFraction * w * h)));
  const kept = new Uint32Array(count + 1);
  for (let i = 0; i < labels.length; i += 1) if (labels[i] && out[i]) kept[labels[i]] += 1;
  for (let i = 0; i < labels.length; i += 1) {
    const l = labels[i];
    if (l && kept[l] < size[l] / 2) out[i] = 1;
  }
  return out;
}

/**
 * The preview the threshold panel shows, which is what "Apply to mask" adds:
 * bandMask with the particles' holes filled, less every particle that
 * reaches into `excludeRect` and, with a size bound, every one outside it.
 * {mask, rejected (null with neither a corner nor a size bound), particles
 * (null without a size bound), small, large, corner}.
 */
export function thresholdPreview(gray, w, h, { min, max, outside = false, colour = null, tolerance, hsv = null,
                                               excludeRect = null, minPx = null, maxPx = null, allow = null } = {}) {
  const passed = bandMask(gray, w, h, { min, max, outside, colour, tolerance, hsv, allow });
  return previewFrom(passed, w, h, { gray, min, max, outside, excludeRect, minPx, maxPx, allow });
}

/**
 * The preview from what passed a method's own test (bandMask's band, or
 * utils/masks/hsb.hsbMask's HSB ranges): its particles' holes filled, cut
 * half-way to their rims when the band is kept outside (`gray` then needed),
 * then the corner and the size bound, as thresholdPreview describes.
 */
export function previewFrom(passed, w, h, { gray = null, min = 0, max = 255, outside = false, excludeRect = null,
                                            minPx = null, maxPx = null, allow = null } = {}) {
  const found = fillSmallHoles(passed, w, h, Math.max(1, Math.round(DETECT.maxHoleFraction * w * h)));
  const filled = outside && gray ? halfwayEdges(gray, found, w, h, (min + max) / 2) : found;
  // A hole filled or an edge moved never reaches past what is allowed.
  if (allow) for (let i = 0; i < filled.length; i += 1) if (!allow[i]) filled[i] = 0;
  const sized = minPx !== null || maxPx !== null;
  if (!sized && !excludeRect) {
    return { mask: filled, rejected: null, particles: null, small: 0, large: 0, corner: 0 };
  }
  const { labels, sizes, count } = labelComponents(filled, w, h, 8);
  const inCorner = excludeRect ? labelsInRect(labels, w, h, count, excludeRect) : null;
  const keep = new Uint8Array(count + 1);
  let kept = 0;
  let small = 0;
  let large = 0;
  let corner = 0;
  for (let l = 1; l <= count; l += 1) {
    if (inCorner && inCorner[l]) corner += 1;
    else if (minPx !== null && sizes[l] < minPx) small += 1;
    else if (maxPx !== null && sizes[l] > maxPx) large += 1;
    else {
      keep[l] = 1;
      kept += 1;
    }
  }
  const mask = new Uint8Array(w * h);
  const rejected = new Uint8Array(w * h);
  for (let i = 0; i < labels.length; i += 1) {
    const l = labels[i];
    if (!l) continue;
    if (keep[l]) mask[i] = 1;
    else rejected[i] = 1;
  }
  return { mask, rejected, particles: sized ? kept : null, small, large, corner };
}

/**
 * An area typed on the page -- um^2 with a scale (umPerPx), image px^2
 * without -- in analysis-grid pixels (`gridScale` = image width / grid width).
 * null for an empty, negative or unreadable value.
 */
export function areaToGridPx(area, { umPerPx = null, gridScale = 1 } = {}) {
  if (area === null || area === undefined || area === '') return null;
  const a = Number(area);
  if (!Number.isFinite(a) || a < 0) return null;
  const imagePx = umPerPx ? a / (umPerPx * umPerPx) : a;
  return imagePx / (gridScale * gridScale);
}

/** areaToGridPx the other way: `px` analysis-grid pixels as an area in um^2 (with umPerPx) or image px^2. */
export function gridPxToArea(px, { umPerPx = null, gridScale = 1 } = {}) {
  const imagePx = px * gridScale * gridScale;
  return umPerPx ? imagePx * umPerPx * umPerPx : imagePx;
}
